import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import type { RecipientUser } from '~/components/messages/RecipientPicker';
import { useAnalytics } from './analytics';

/**
 * Messaging dock state — LinkedIn-style multi-panel layout.
 *
 * On desktop (md+) the dock renders as two cooperating panels pinned to
 * the bottom-right: a **main dock** (list / compose) and, when a thread
 * is active, a separate **thread panel** to its LEFT. Each panel has its
 * own minimized/expanded state so users can park a thread for later
 * without losing their place in the inbox.
 *
 * On mobile the same data model is rendered as a single-panel stack: the
 * thread panel, when present, visually takes over; otherwise the main
 * panel is shown. Back/close collapses naturally.
 */

export type MainView = 'list' | 'compose';

interface MainFrame {
  view: MainView;
  prefillRecipientAsfId?: string;
  /** When set, compose seeds To without waiting on messageableUsers cache. */
  prefillRecipient?: RecipientUser;
  prefillBody?: string;
}

interface ThreadPanel {
  threadId: string;
  isExpanded: boolean;
}

interface MessagingDockState {
  mainFrame: MainFrame;
  mainIsExpanded: boolean;
  threadPanel: ThreadPanel | null;
}

interface MessagingDockAPI extends MessagingDockState {
  openList: (opts?: { source?: string }) => void;
  openCompose: (opts?: { recipientAsfId?: string; recipient?: RecipientUser; body?: string; source?: string }) => void;
  openThread: (threadId: string, opts?: { source?: string }) => void;
  closeThread: () => void;
  minimizeThread: () => void;
  expandThread: (opts?: { source?: string }) => void;
  minimizeMain: () => void;
  expandMain: () => void;
  toggleMain: () => void;
  backFromCompose: () => void;

  // Live compose textarea contents, mirrored from DockComposeView via a ref
  // (not state) so keystrokes never trigger a re-render/remount here. Lets
  // callers (e.g. "open in full page") hand off the text the user actually
  // typed instead of the stale prefill from when compose was first opened.
  getComposeDraft: () => string;
  setComposeDraft: (body: string) => void;

  // Convenience for the single-panel mobile rendering path. When a
  // thread is open, mobile shows the thread and "back" closes it; else
  // mobile shows the main panel.
  mobileActivePanel: 'thread' | 'main';
}

const DEFAULT_MAIN: MainFrame = { view: 'list' };

const MessagingDockContext = createContext<MessagingDockAPI | null>(null);

export function MessagingDockProvider({ children }: { children: React.ReactNode }) {
  const {
    amplitude: { track },
  } = useAnalytics();

  const [mainFrame, setMainFrame] = useState<MainFrame>(DEFAULT_MAIN);
  const [mainIsExpanded, setMainIsExpanded] = useState(false);
  const [threadPanel, setThreadPanel] = useState<ThreadPanel | null>(null);

  // Mirror the latest threadPanel into a ref so callbacks that need to read
  // the current threadId (e.g. for analytics) don't have to close over the
  // state value and churn their identity on every panel change.
  const threadPanelRef = useRef<ThreadPanel | null>(threadPanel);
  threadPanelRef.current = threadPanel;

  const composeDraftRef = useRef<string>('');
  const getComposeDraft = useCallback(() => composeDraftRef.current, []);
  const setComposeDraft = useCallback((body: string) => {
    composeDraftRef.current = body;
  }, []);

  const openList = useCallback(
    (opts?: { source?: string }) => {
      if (opts?.source) {
        track('dm_dock_main_expanded', { source: opts.source });
      }
      setMainFrame({ view: 'list' });
      setMainIsExpanded(true);
    },
    [track]
  );

  const openCompose = useCallback(
    (opts?: { recipientAsfId?: string; recipient?: RecipientUser; body?: string; source?: string }) => {
      if (opts?.source) {
        track('dm_dock_compose_opened', { source: opts.source });
      }
      const recipient = opts?.recipient;
      // Don't write composeDraftRef here: when the recipient/body match the
      // current compose session, DockComposeView won't remount, so this
      // would clobber the ref with the new prefill while the textarea keeps
      // showing the user's in-progress edits. DockComposeView's own mirror
      // effect (mount or content change) keeps the ref in sync instead.
      setMainFrame({
        view: 'compose',
        prefillRecipientAsfId: recipient?.asfId ?? opts?.recipientAsfId,
        prefillRecipient: recipient,
        prefillBody: opts?.body,
      });
      setMainIsExpanded(true);
    },
    [track]
  );

  const backFromCompose = useCallback(() => {
    composeDraftRef.current = '';
    setMainFrame({ view: 'list' });
  }, []);

  const openThread = useCallback(
    (threadId: string, opts?: { source?: string }) => {
      if (opts?.source) {
        track('dm_dock_thread_opened', { thread_id: threadId, source: opts.source });
      }
      // Thread always opens in its own panel, expanded. Clicking a second
      // thread swaps the one open thread panel (LinkedIn supports stacking
      // multiple; can be added later). If the main dock was on compose,
      // snap it back to the list so the user sees their inbox + new thread
      // side-by-side after sending a message.
      setThreadPanel({ threadId, isExpanded: true });
      setMainFrame((prev) => (prev.view === 'compose' ? { view: 'list' } : prev));
      setMainIsExpanded(true);
    },
    [track]
  );

  const closeThread = useCallback(() => {
    setThreadPanel(null);
  }, []);

  const minimizeThread = useCallback(() => {
    setThreadPanel((prev) => (prev ? { ...prev, isExpanded: false } : prev));
  }, []);

  const expandThread = useCallback(
    (opts?: { source?: string }) => {
      const current = threadPanelRef.current;
      if (opts?.source && current) {
        track('dm_dock_thread_opened', { thread_id: current.threadId, source: opts.source });
      }
      setThreadPanel((prev) => (prev ? { ...prev, isExpanded: true } : prev));
    },
    [track]
  );

  const minimizeMain = useCallback(() => {
    setMainIsExpanded(false);
  }, []);

  const expandMain = useCallback(() => {
    setMainIsExpanded(true);
  }, []);

  const toggleMain = useCallback(() => {
    setMainIsExpanded((prev) => !prev);
  }, []);

  const mobileActivePanel: 'thread' | 'main' = threadPanel ? 'thread' : 'main';

  const api = useMemo<MessagingDockAPI>(
    () => ({
      mainFrame,
      mainIsExpanded,
      threadPanel,
      mobileActivePanel,
      openList,
      openCompose,
      openThread,
      closeThread,
      minimizeThread,
      expandThread,
      minimizeMain,
      expandMain,
      toggleMain,
      backFromCompose,
      getComposeDraft,
      setComposeDraft,
    }),
    [
      mainFrame,
      mainIsExpanded,
      threadPanel,
      mobileActivePanel,
      openList,
      openCompose,
      openThread,
      closeThread,
      minimizeThread,
      expandThread,
      minimizeMain,
      expandMain,
      toggleMain,
      backFromCompose,
      getComposeDraft,
      setComposeDraft,
    ]
  );

  return <MessagingDockContext.Provider value={api}>{children}</MessagingDockContext.Provider>;
}

export function useMessagingDock(): MessagingDockAPI {
  const ctx = useContext(MessagingDockContext);
  if (!ctx) {
    throw new Error('useMessagingDock must be used inside <MessagingDockProvider>');
  }
  return ctx;
}

/**
 * Non-throwing variant for components that may render outside dashboard
 * routes (e.g. shared headers) and need to silently no-op when the dock
 * isn't mounted.
 */
export function useMessagingDockOptional(): MessagingDockAPI | null {
  return useContext(MessagingDockContext);
}
